import React from "react";
import "./Style.css";
import MetaDataContent from "./MetaDataContent";
import MailRecipientsCell from "./MailRecipientsCell";
import { convertEpochToDateFormat } from "../../Utils/convertEpochToDateFormat";
import { formatAssignedToDisplay } from "../../Utils/formatAssignedToDisplay";
import {
  McSelect,
  McOption,
} from "@maersk-global/mds-react-wrapper/components-core";
import UserPickerSelect from "../UserPickerSelect";

const PRIORITY_LABEL = { "0": "P0", "1": "P1", "2": "P2", "3": "P3", "4": "P4" };

const formatMinutes = (mins) => {
  if (mins === undefined || mins === null || mins === "") return "—";
  const n = parseFloat(mins);
  if (isNaN(n)) return "—";
  if (n < 60) return `${Math.round(n)} min`;
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

function MetaData(props) {
  const { priorityEditable = false, onPriorityChange, assignedToEditable = false, onAssignedToChange } = props;

  const headings = {
    incidentID: "Incident Number",
    priority: "Priority",
    createdOn: "Created On",
    cmdb_ci: "Affected Application",
    assigned_to: "Assigned To",
    state: "State",
    platform: "Platform",
    portfolio: "Portfolio",
    product: "Product",
    app: "Application",
    owningBusinessUnit: "Business Unit",
    caller_id: "Caller ID",
    channelName: "Channel Name",
  };

  const skipKeys = [
    "pmtId",
    "problemID",
    "caller_id",
    "smData",
    "resolvedOn",
    "priorityEditable",
    "onPriorityChange",
    "assignedToEditable",
    "onAssignedToChange",
    "mttd",
    "mttrecover",
    "impactedByChange",
    "short_description",
    "postMortemId",
    "portfolio",
    "platform",
    "isDraft",
    "recipientsRefreshTick",
  ];

  const capturedByAlert = props.channelName?.toLowerCase().includes("alert");
  const mttd = props.mttd;
  const mttrecover = props.mttrecover;
  const impactedByChange = props.impactedByChange;
  const causedByChange = impactedByChange === true || impactedByChange === "true" || impactedByChange === "Yes";
  const mttdColorStyle = (() => {
    const n = parseFloat(mttd);
    if (isNaN(n)) return {};
    return n <= 15
      ? { color: "var(--mds_brand_appearance_success_default_background-color, #27ae60)", fontWeight: 600 }
      : { color: "var(--mds_brand_appearance_error_default_background-color, #c0392b)", fontWeight: 600 };
  })();
  const mttrColorStyle = (() => {
    const n = parseFloat(mttrecover);
    if (isNaN(n)) return {};
    return n <= 30
      ? { color: "var(--mds_brand_appearance_success_default_background-color, #27ae60)", fontWeight: 600 }
      : { color: "var(--mds_brand_appearance_error_default_background-color, #c0392b)", fontWeight: 600 };
  })();

  const priorityLabel = PRIORITY_LABEL[String(props.priority)] || (props.priority ? `P${props.priority}` : "");
  const appName = props.app || props.product || "";
  const headerTitle = props.short_description || appName;

  const snowLink = process.env.REACT_APP_SNOW_LINK;

  return (
    <div className="metaData_header third-step">
      <div className="sre-section-header" style={{ margin: "-16px -20px 16px -20px", borderRadius: "3px 3px 0 0" }}>
        <span className="sre-section-header-title" style={{ fontSize: "14px", letterSpacing: "0.04em" }}>
          {headerTitle}
        </span>
      </div>
      <div className="metaData_contentWrapper">
        <div className="metaData_contentGridWrapper">
          <div className="metaData_contentGrid">
            {Object.keys(props).map(
              (key) =>
                !skipKeys.includes(key) && key !== "assigned_to" && (
                  <div
                    key={key}
                    className={`metaData_col ${key === "channelName" ? "span-2-cols" : ""}`}
                  >
                    {key === "priority" && priorityEditable ? (
                      <>
                        <div className="metaData_colHead">Priority</div>
                        <div className="metaData_colBody">
                          <McSelect
                            label="Priority"
                            hiddenlabel
                            width="100"
                            placeholder="Select"
                            name="priority"
                            optionswidth="100"
                            value={props[key]}
                            optionselected={(e) => {
                              const newPriority = e?.detail?.value;
                              if (
                                onPriorityChange &&
                                newPriority &&
                                newPriority !== props[key]
                              ) {
                                onPriorityChange(newPriority);
                              }
                            }}
                          >
                            <McOption value="0">P0</McOption>
                            <McOption value="1">P1</McOption>
                            <McOption value="2">P2</McOption>
                          </McSelect>
                        </div>
                      </>
                    ) : (
                      <MetaDataContent
                        heading={headings[key]}
                        data={
                          key === "createdOn"
                            ? convertEpochToDateFormat(props[key])
                            : key === "incidentID"
                                ? (props[key] ? (
                                    snowLink ? (
                                      <a
                                        href={`${snowLink}number=${props[key]}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#007bff", textDecoration: "underline", textUnderlineOffset: "2px" }}
                                      >
                                        {props[key]}
                                      </a>
                                    ) : props[key]
                                  ) : "NA")
                                : props[key]
                        }
                      />
                    )}
                  </div>
                )
            )}
            {assignedToEditable ? (
              <div className="metaData_col" style={{ overflow: 'visible', gridColumn: '1 / -1' }}>
                <div className="metaData_colHead">Postmortem Assignee <span style={{ color: 'var(--mds_brand_appearance_error_default_text-color, #c0392b)' }}>*</span></div>
                <div className="metaData_colBody" style={{ overflow: 'visible', whiteSpace: 'normal' }}>
                  <div style={{ width: "100%" }}>
                    <UserPickerSelect
                      value={props.assigned_to || ""}
                      onChange={(email, displayName) => {
                        if (onAssignedToChange) {
                          onAssignedToChange(email);
                        }
                      }}
                      placeholder="Type name to search..."
                      required
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="metaData_col">
                <MetaDataContent
                  heading="Postmortem Assignee"
                  data={formatAssignedToDisplay(props.assigned_to)}
                />
              </div>
            )}
            <div className="metaData_col" style={{ gridColumn: '1 / -1' }}>
              <MailRecipientsCell
                postMortemId={props.postMortemId}
                portfolio={props.portfolio}
                platform={props.platform}
                isEditable={props.isDraft}
                refreshTick={props.recipientsRefreshTick}
              />
            </div>
          </div>
        </div>
        <div className="metaData_kpi_strip">
          <div className="metaData_col">
            <div className="metaData_colHead">Captured by Alert</div>
            <div className="metaData_colBody" style={capturedByAlert
              ? { color: "var(--mds_brand_appearance_success_default_background-color, #27ae60)", fontWeight: 600 }
              : { color: "var(--mds_brand_appearance_error_default_background-color, #c0392b)", fontWeight: 600 }}>
              {capturedByAlert ? "Yes" : "No"}
            </div>
          </div>
          <div className="metaData_col">
            <div className="metaData_colHead">Mean Time to Detect</div>
            <div className="metaData_colBody" style={mttdColorStyle}>
              {formatMinutes(mttd)}
            </div>
          </div>
          <div className="metaData_col">
            <div className="metaData_colHead">Mean Time to Recover</div>
            <div className="metaData_colBody" style={mttrColorStyle}>
              {formatMinutes(mttrecover)}
            </div>
          </div>
          <div className="metaData_col">
            <div className="metaData_colHead">Caused by Change</div>
            <div className="metaData_colBody" style={causedByChange
              ? { color: "var(--mds_brand_appearance_error_default_background-color, #c0392b)", fontWeight: 600 }
              : { color: "var(--mds_brand_appearance_success_default_background-color, #27ae60)", fontWeight: 600 }}>
              {causedByChange ? "Yes" : "No"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MetaData;
